import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, catchError, map, throwError } from 'rxjs';
import { Empresa, EmpresaCreate, EmpresaUpdate } from '../models/empresa.model';
import { API_ENDPOINTS } from '../config/api.config';

export interface ImportacionProductosResumen {
  totalFilas: number;
  productosCreados: number;
  categoriasCreadas: number;
  errores: Array<{ fila: number; codigo?: string; mensajes: string[] }>;
  detalles?: string;
}

@Injectable({
  providedIn: 'root',
})
export class EmpresaService {
  private apiUrl = API_ENDPOINTS.empresas;

  private httpOptions = {
    headers: new HttpHeaders({
      'Content-Type': 'application/json',
    }),
  };

  constructor(private http: HttpClient) {}

  private normalizeEmpresa(empresa: any): Empresa {
    return {
      ...empresa,
      anuncios: Array.isArray(empresa?.anuncios) ? empresa.anuncios : [],
      licencia: empresa?.licencia
        ? {
            ...empresa.licencia,
            fecha_inicio: empresa.licencia.fecha_inicio,
            fecha_fin: empresa.licencia.fecha_fin,
            active: empresa.licencia.active ?? empresa.licencia.activa,
            activa: empresa.licencia.activa ?? empresa.licencia.active,
          }
        : undefined,
    } as Empresa;
  }

  /**
   * Obtiene todas las empresas
   */
  getEmpresas(): Observable<Empresa[]> {
    return this.http
      .get<any>(this.apiUrl + '/super-usuario/ver-lista', this.httpOptions)
      .pipe(
        map((response) => {
          const empresasRaw = Array.isArray(response)
            ? response
            : response?.Empresa || response?.empresas || response?.data || [];

          return (empresasRaw as any[]).map((empresa) =>
            this.normalizeEmpresa(empresa),
          );
        }),
        catchError(this.handleError),
      );
  }

  editEmpresa(empresa: EmpresaUpdate): Observable<EmpresaUpdate> {
    const { id, ...updateData } = empresa;
    return this.http
      .put<EmpresaUpdate>(
        `${this.apiUrl}/super-usuario/editar/${id}`,
        updateData,
        this.httpOptions,
      )
      .pipe(
        map((response) => {
          return {
            ...response,
          };
        }),
        catchError(this.handleError),
      );
  }

  renovarLicencia(
    empresaId: number,
    vencimiento: string,
  ): Observable<EmpresaUpdate> {
    return this.editEmpresa({ id: empresaId, vencimiento });
  }

  createEmpresa(empresa: EmpresaCreate): Observable<EmpresaCreate> {
    return this.http
      .post<EmpresaCreate>(
        this.apiUrl + '/super-usuario/crear',
        empresa,
        this.httpOptions,
      )
      .pipe(
        map((response) => {
          return {
            ...response,
          };
        }),
        catchError((error) => {
          console.error('createEmpresa - Error capturado:', error);
          console.error('createEmpresa - Status del error:', error.status);
          console.error('createEmpresa - Error completo:', error.error);
          return this.handleError(error);
        }),
      );
  }

  deleteEmpresa(id: number): Observable<void> {
    return this.http
      .delete<void>(
        `${this.apiUrl}/super-usuario/eliminar/${id}`,
        this.httpOptions,
      )
      .pipe(catchError(this.handleError));
  }

  importarProductosExcel(
    empresaId: number,
    sucursalId: number,
    archivoExcel: File,
  ): Observable<ImportacionProductosResumen> {
    const formData = new FormData();
    formData.append('empresa_id', String(empresaId));
    formData.append('sucursal_id', String(sucursalId));
    formData.append('archivo_excel', archivoExcel);

    return this.http
      .post<ImportacionProductosResumen>(
        `${this.apiUrl}/super-usuario/importar-productos`,
        formData,
      )
      .pipe(catchError(this.handleError));
  }

  /**
   * Manejo de errores
   */
  private handleError(error: any): Observable<never> {
    let errorResponse = {
      success: false,
      message: 'Ocurrió un error desconocido',
      code: '',
      details: null,
    };

    if (error.error instanceof ErrorEvent) {
      // Error del lado del cliente
      errorResponse = {
        success: false,
        message: error.error.message,
        code: 'CLIENT_ERROR',
        details: error.error,
      };
    } else {
      // Error del servidor
      errorResponse = {
        success: false,
        message: error.error?.message || error.message || 'Error del servidor',
        code: error.status || 'SERVER_ERROR',
        details: error.error || error,
      };
    }
    // Debug
    // console.error('Error en EmpresaService:', JSON.stringify(errorResponse, null, 2));
    return throwError(() => errorResponse);
  }
}
